import type { FC } from 'react'
import { Suspense, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArtworkDetailMembershipAdUnit } from 'src/components/AdUnit/DetailAdUnit'
import { ArtworkRelevantArtworksInlineForDetailsSection } from 'src/components/Artwork/ArtworkRelevantArtworks'
import type {
  Artwork,
  ArtworkDetailFragment,
  ArtworkWithTaskDetailBaseFragment,
  DetailedTaskParameters,
} from 'src/services/apollo/generated/graphql'
import { useGetGenerationModelByVersionIdForArtwork } from 'src/services/apollo/generation-model'
import { cx } from 'src/utils/cx'
import { isModelStub } from 'src/utils/generationModel/modelPermissions'
import Text from '~/components/ui/Text'
import ArtworkDetailContentModels from './ArtworkDetailContentModels'
import ArtworkDetailCreationAssistant from './ArtworkDetailCreationAssistant'
import { ArtworkDeriveButtons } from './ArtworkDetailDeriveButtons'
import { ArtworkParameterInfo } from './ArtworkParameterInfo'
import I2vModelUsedDisplay from './I2vModelUsedDisplay'
import type { ParsedParamsForArtworkDetail } from './parser'
import { useNarrowScreenForArtworkDetail } from './utils'

const ArtworkDetailContent: FC<
  {
    artwork: ArtworkDetailFragment
    artworkLoras: ArtworkWithTaskDetailBaseFragment['loras']
    parsedTaskParameters?: ParsedParamsForArtworkDetail
    /**
     * @deprecated Migrate to parsedArtworkParameters
     */
    taskParameters?: DetailedTaskParameters
    isSinglePage?: boolean
  } & StyleProps
> = ({
  artwork,
  artworkLoras,
  taskParameters,
  parsedTaskParameters,
  className,
  isSinglePage,
}) => {
  const { t } = useTranslation(['artwork', 'user', 'common', 'action'])
  const narrowScreen = useNarrowScreenForArtworkDetail(isSinglePage)

  // only show relevant artworks inline for details section on non-single page
  // because for single page we have a full width row (in the sibling of this FC)
  const shouldShowRelevantArtworksInlineForDetailsSection = !isSinglePage

  const taskType = parsedTaskParameters?.type
  // Only generator type tasks have a modelId that should be queried
  const taskModelId =
    taskType === 'generator' ? parsedTaskParameters?.modelId : undefined

  const i2vModelForT2i2v =
    taskType === 't2i2v' ? parsedTaskParameters?.t2i2v.model : undefined

  return (
    <div
      className={cx(
        'flex-1 flex-shrink min-w-0 flex flex-col gap-5',
        className,
      )}
    >
      {narrowScreen ? null : (
        <ArtworkDeriveButtons
          artwork={artwork}
          parsedTaskParameters={parsedTaskParameters}
          taskParameters={taskParameters}
          isSinglePage={isSinglePage}
        />
      )}

      {/* Artwork Parameters */}
      <ArtworkParameterInfo
        artwork={artwork}
        artworkLoras={artworkLoras}
        taskParameters={taskParameters}
      />

      {/* Model info */}
      {(artworkLoras?.length || taskModelId || i2vModelForT2i2v) && (
        <>
          <div className="mt-3">
            <h3 className="font-medium">
              {t('artwork:model.model-lora-used')}
            </h3>
            {taskModelId && (
              <PrivateModelCount loras={artworkLoras} versionId={taskModelId} />
            )}
          </div>

          {i2vModelForT2i2v && <I2vModelUsedDisplay model={i2vModelForT2i2v} />}
          <ArtworkDetailContentModels
            loras={artworkLoras}
            modelId={taskModelId}
          />
        </>
      )}

      {/* Creation Assistant */}
      {artwork.extra?.internalCaller && (
        <ArtworkDetailCreationAssistant
          internalCaller={artwork.extra.internalCaller}
        />
      )}

      {shouldShowRelevantArtworksInlineForDetailsSection && (
        <div className="flex flex-col bg-background">
          <ArtworkRelevantArtworksInlineForDetailsSection
            artworkId={artwork.id}
          />
        </div>
      )}

      <ArtworkDetailMembershipAdUnit
        imgClassName="object-contain rounded-2xl"
        className="rounded-2xl border hover:shadow-xl transition ease-out hover:border-neutral-400 dark:border-none"
      />
    </div>
  )
}

function PrivateModelCount(props: {
  versionId: string
  loras: Artwork['loras']
}) {
  // useGetGenerationModelByVersionIdForArtwork suspends on cache miss in Apollo 4.
  // Without this boundary, the suspension bubbles up to ArtworkDetailDialog's
  // <Suspense fallback={<LoadingPage />}>, showing a full-page loader.
  return (
    <Suspense>
      <PrivateModelCountContent {...props} />
    </Suspense>
  )
}

function PrivateModelCountContent(props: {
  versionId: string
  loras: Artwork['loras']
}) {
  const { versionId, loras } = props
  const { data, loading } =
    useGetGenerationModelByVersionIdForArtwork(versionId)
  const privateModelsCount = useMemo(() => {
    // If the main model is nullish, then it's private
    const modelIsStub = !loading && data?.model == null
    const stubLoras = [...(loras ?? [])].filter(m => m && isModelStub(m))
    return stubLoras.length + (modelIsStub ? 1 : 0)
  }, [loras, data?.model, loading])

  if (privateModelsCount === 0) return null

  return (
    <p className="text-sm italic">
      <Text
        i18nKey="artwork:model.model-hidden-count.title"
        values={{ count: privateModelsCount }}
      />
    </p>
  )
}

export default ArtworkDetailContent
